import { create } from "zustand";
import { clients as seed } from "@/mocks/seed";
import type { Client } from "@/types";
import type { ClientFormValues } from "./components/ClientFormModal";

/** Local mock store standing in for the API (Phase 5 swaps to TanStack Query).
 *  "Delete" is a soft archive — kept out of the active list but not destroyed. */
interface ClientsState {
  active: Client[];
  archived: Client[];
  add: (values: ClientFormValues) => Client;
  update: (id: string, values: ClientFormValues) => void;
  archive: (id: string) => void;
  restore: (id: string) => void;
  getById: (id: string) => Client | undefined;
}

function clean(value: string | undefined): string | undefined {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : undefined;
}

export const useClientsStore = create<ClientsState>((set, get) => ({
  active: seed,
  archived: [],
  add: (values) => {
    const client: Client = {
      id: `c_${crypto.randomUUID().slice(0, 8)}`,
      revenue: 0,
      adSpendUsd: 0,
      walletUsd: 0,
      since: new Date().toLocaleDateString("fr-FR", { month: "short", year: "numeric" }),
      createdAt: new Date().toISOString(),
      ...values,
      email: clean(values.email),
      phone: clean(values.phone),
      niche: clean(values.niche),
      facebookUrl: clean(values.facebookUrl),
      instagramUrl: clean(values.instagramUrl),
      tiktokUrl: clean(values.tiktokUrl),
      startDate: clean(values.startDate),
      notes: clean(values.notes),
    };
    set((s) => ({ active: [client, ...s.active] }));
    return client;
  },
  update: (id, values) =>
    set((s) => ({
      active: s.active.map((c) =>
        c.id === id
          ? {
              ...c,
              ...values,
              email: clean(values.email),
              phone: clean(values.phone),
              niche: clean(values.niche),
              facebookUrl: clean(values.facebookUrl),
              instagramUrl: clean(values.instagramUrl),
              tiktokUrl: clean(values.tiktokUrl),
              startDate: clean(values.startDate),
              notes: clean(values.notes),
            }
          : c,
      ),
    })),
  archive: (id) =>
    set((s) => {
      const target = s.active.find((c) => c.id === id);
      if (!target) return s;
      return { active: s.active.filter((c) => c.id !== id), archived: [...s.archived, target] };
    }),
  restore: (id) =>
    set((s) => {
      const target = s.archived.find((c) => c.id === id);
      if (!target) return s;
      return { archived: s.archived.filter((c) => c.id !== id), active: [target, ...s.active] };
    }),
  getById: (id) => get().active.find((c) => c.id === id) ?? get().archived.find((c) => c.id === id),
}));
